// Cadastro de tutoriais (PO1-3505, épico PO1-3504): catálogo GLOBAL que alimenta
// a Central de Treinamento, o ícone "Como usar esta tela" e o Autopilot. Mora no
// Everlog Admin → Catálogos. Só o admin Everlog cria, edita, ativa e inativa (o
// backend devolve 403 para os demais e aqui os botões somem). Sem exclusão:
// inativar tira o tutorial de todo lugar.
// Telas, capítulos e passos são editados como LINHAS (adicionar/remover), nunca
// como JSON cru. O PDF do manual sobe em separado (só com o tutorial salvo).
// Molde: Admin/TiposVeiculo (padrão do épico PO1-2723).
// Sem `?.`/`??`: o build roda em CRA 3.4 / Node 12.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, FormFeedback, FormGroup, Input, Label, Modal, ModalBody, Row, Spinner, Table } from "reactstrap";
import { FaBan, FaCheck, FaPen, FaPlus, FaTrash } from "react-icons/fa";

import PageHero, { PageHeroRefreshButton } from "components/PageHero";
import FilterBand from "components/FilterBand";
import ShellCard, { TableSurface } from "components/ShellCard";
import SortHeader from "components/SortHeader";
import Status from "components/Status";
import TablePagination from "components/TablePagination";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import { useAuth } from "hooks/auth";
import { useToast } from "hooks/toast";
import useDebounce from "hooks/useDebounce";
import {
  MANUAL_LEGENDA,
  alterarStatusTutorial,
  atualizarTutorial,
  criarTutorial,
  enviarManual,
  listarTutoriaisAdmin,
  mensagemErroTreinamento,
  validarArquivoManual,
} from "services/treinamentoApi";
import { paginate, sortRows, toggleSort } from "utils/tableHelpers";
import { PRODUTOS_TUTORIAL, PRODUTO_ROTULO, formatarDuracao } from "views/Treinamento/rotas";
import { LIMITES, ativo, criarFormVazio, formDoTutorial, payloadDoForm, validarForm } from "./regras";

import "./CatalogoTutoriais.css";

const DEFAULT_SORT = { field: "ordem", dir: "asc" };
const MODAL_CLASS = "modal-align-top catalogo-tutoriais-modal";

function normalizar(texto) {
  return String(texto || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

function sortValue(row, field) {
  if (field === "status") return ativo(row) ? 1 : 0;
  if (field === "ordem") return Number(row.ordem) || 0;
  if (field === "produtos") return (row.produtos || []).join(",");
  return row[field];
}

const stateBoxStyle = { textAlign: "center", padding: "2.5rem 1rem", color: AUTOPILOT_COLORS.muted };
const mutedCellStyle = { color: AUTOPILOT_COLORS.muted };

function CabecalhoModal({ titulo, onFechar }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "var(--everlog-card-hero-bg)",
        borderBottom: "1px solid var(--everlog-card-border)",
        padding: "16px 20px",
      }}
    >
      <span style={{ color: "var(--everlog-card-title)", fontWeight: 600, fontSize: 15 }}>{titulo}</span>
      <button
        type="button"
        onClick={onFechar}
        style={{ background: "none", border: "none", color: "var(--everlog-card-title)", fontSize: 22, lineHeight: 1, cursor: "pointer", padding: "0 4px" }}
        aria-label="Fechar"
      >
        ×
      </button>
    </div>
  );
}

export default function CatalogoTutoriais() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const podeEditar = Boolean(user && user.setor === "admin");

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const [produtoFiltro, setProdutoFiltro] = useState("");
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [isFormOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(criarFormVazio);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [arquivoManual, setArquivoManual] = useState(null);
  const [erroManual, setErroManual] = useState("");
  const [enviandoManual, setEnviandoManual] = useState(false);
  const inputArquivoRef = useRef(null);

  const debouncedSearch = useDebounce(searchTerm, 250);

  const toastErro = useCallback(
    (title, description) => addToast({ place: "tr", type: "danger", title, description, icon: "tim-icons icon-alert-circle-exc" }),
    [addToast],
  );
  const toastOk = useCallback(
    (title, description) => addToast({ place: "tr", type: "success", title, description, icon: "tim-icons icon-check-2" }),
    [addToast],
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      setData(await listarTutoriaisAdmin());
    } catch (error) {
      setData([]);
      const mensagem = mensagemErroTreinamento(error, "Não foi possível buscar os tutoriais.");
      setLoadError(mensagem);
      toastErro("Erro ao carregar", mensagem);
    } finally {
      setLoading(false);
    }
  }, [toastErro]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filtered = useMemo(() => {
    const query = normalizar(debouncedSearch);
    return (Array.isArray(data) ? data : []).filter((item) => {
      if (!mostrarInativos && !ativo(item)) return false;
      if (produtoFiltro && (item.produtos || []).indexOf(produtoFiltro) === -1) return false;
      if (query && normalizar([item.codigo, item.titulo, item.resumo].filter(Boolean).join(" ")).indexOf(query) === -1) return false;
      return true;
    });
  }, [data, debouncedSearch, mostrarInativos, produtoFiltro]);

  const sorted = useMemo(() => sortRows(filtered, sort, sortValue), [filtered, sort]);
  const paged = useMemo(() => paginate(sorted, page, pageSize), [sorted, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, mostrarInativos, produtoFiltro, sort, pageSize]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setForm(criarFormVazio());
    setFormErrors({});
    setArquivoManual(null);
    setErroManual("");
    setFormOpen(true);
  }, []);

  const openEdit = useCallback((item) => {
    setEditing(item);
    setForm(formDoTutorial(item));
    setFormErrors({});
    setArquivoManual(null);
    setErroManual("");
    setFormOpen(true);
  }, []);

  const closeForm = useCallback(() => {
    if (saving || enviandoManual) return;
    setFormOpen(false);
  }, [saving, enviandoManual]);

  // O valor é lido fora do updater (React 16: o evento sintético volta ao pool).
  const updateForm = useCallback((field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFormErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  }, []);

  const toggleProduto = useCallback((codigo, marcado) => {
    setForm((current) => {
      const atual = current.produtos || [];
      const semEle = atual.filter((p) => p !== codigo);
      return { ...current, produtos: marcado ? semEle.concat([codigo]) : semEle };
    });
    setFormErrors((current) => {
      if (!current.produtos) return current;
      const next = { ...current };
      delete next.produtos;
      return next;
    });
  }, []);

  // Linhas de capítulos/telas/passos.
  const updateLinha = useCallback((lista, i, campo, valor) => {
    setForm((current) => {
      const itens = (current[lista] || []).slice();
      itens[i] = campo === null ? valor : { ...itens[i], [campo]: valor };
      return { ...current, [lista]: itens };
    });
    setFormErrors((current) => {
      const chave = `${lista}.${i}`;
      if (!current[chave]) return current;
      const next = { ...current };
      delete next[chave];
      return next;
    });
  }, []);
  const addLinha = useCallback((lista, vazio) => {
    setForm((current) => ({ ...current, [lista]: (current[lista] || []).concat([vazio]) }));
  }, []);
  const removeLinha = useCallback((lista, i) => {
    setForm((current) => ({ ...current, [lista]: (current[lista] || []).filter((_, k) => k !== i) }));
    setFormErrors((current) => {
      const next = {};
      Object.keys(current).forEach((k) => {
        if (k.indexOf(`${lista}.`) !== 0) next[k] = current[k];
      });
      return next;
    });
  }, []);

  const handleSave = useCallback(async () => {
    const nextErrors = validarForm(form);
    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      return;
    }
    const payload = payloadDoForm(form);
    // Na edição o status muda pelo botão Ativar/Inativar da lista.
    if (editing) delete payload.status;
    try {
      setSaving(true);
      let salvo;
      if (editing) salvo = await atualizarTutorial(editing.id, payload);
      else salvo = await criarTutorial(payload);
      toastOk(editing ? "Tutorial atualizado" : "Tutorial cadastrado", editing ? "As alterações foram salvas." : "O tutorial foi cadastrado.");
      if (!editing && salvo && salvo.id) {
        // Continua no modal para subir o PDF, agora com o id.
        setEditing(salvo);
        setForm(formDoTutorial(salvo));
      } else {
        setFormOpen(false);
      }
      loadData();
    } catch (error) {
      toastErro("Erro ao salvar", mensagemErroTreinamento(error, "Não foi possível salvar o tutorial."));
    } finally {
      setSaving(false);
    }
  }, [editing, form, loadData, toastErro, toastOk]);

  const handleEscolherArquivo = useCallback((e) => {
    const arquivo = e.target.files && e.target.files[0];
    setArquivoManual(arquivo || null);
    setErroManual(arquivo ? validarArquivoManual(arquivo) || "" : "");
  }, []);

  const handleEnviarManual = useCallback(async () => {
    if (!editing || !arquivoManual) return;
    const erro = validarArquivoManual(arquivoManual);
    if (erro) {
      setErroManual(erro);
      return;
    }
    try {
      setEnviandoManual(true);
      const salvo = await enviarManual(editing.id, arquivoManual);
      toastOk("Manual enviado", `"${arquivoManual.name}" passou a ser o manual deste tutorial.`);
      setArquivoManual(null);
      if (inputArquivoRef.current) inputArquivoRef.current.value = "";
      if (salvo && salvo.id) setEditing(salvo);
      loadData();
    } catch (error) {
      setErroManual(mensagemErroTreinamento(error, "Não foi possível enviar o manual."));
    } finally {
      setEnviandoManual(false);
    }
  }, [editing, arquivoManual, loadData, toastOk]);

  const changeStatus = useCallback(
    async (item, novoStatus) => {
      try {
        setChangingStatus(true);
        await alterarStatusTutorial(item.id, novoStatus);
        toastOk(
          novoStatus ? "Tutorial ativado" : "Tutorial inativado",
          novoStatus ? `"${item.titulo}" voltou a aparecer na Central.` : `"${item.titulo}" saiu da Central, do ícone de ajuda e do Autopilot.`,
        );
        loadData();
      } catch (error) {
        toastErro(novoStatus ? "Não foi possível ativar" : "Não foi possível inativar", mensagemErroTreinamento(error, "Erro ao alterar o status."));
      } finally {
        setChangingStatus(false);
      }
    },
    [loadData, toastErro, toastOk],
  );

  const handleSort = useCallback((field) => setSort((prev) => toggleSort(prev, field)), []);
  const clearFilters = useCallback(() => {
    setSearchTerm("");
    setMostrarInativos(false);
    setProdutoFiltro("");
  }, []);
  const hasFilters = normalizar(searchTerm) !== "" || mostrarInativos || produtoFiltro !== "";
  const inativosOcultos = mostrarInativos ? 0 : (Array.isArray(data) ? data : []).filter((i) => !ativo(i)).length;

  const renderListState = () => {
    if (loading) {
      return (
        <div style={stateBoxStyle}>
          <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent, marginRight: "0.5rem" }} />
          Carregando tutoriais…
        </div>
      );
    }
    if (loadError) {
      return (
        <div style={stateBoxStyle}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 700, marginBottom: "0.35rem" }}>Não foi possível carregar</div>
          <p style={{ marginBottom: "0.75rem" }}>{loadError}</p>
          <Button size="sm" color="info" outline onClick={loadData}>
            Tentar novamente
          </Button>
        </div>
      );
    }
    if (sorted.length === 0) {
      return (
        <div style={stateBoxStyle}>
          <div style={{ color: AUTOPILOT_COLORS.title, fontWeight: 700, marginBottom: "0.35rem" }}>Nenhum tutorial encontrado</div>
          <p style={{ marginBottom: 0 }}>
            {hasFilters ? "Ajuste os filtros ou limpe a busca." : inativosOcultos ? 'Não há tutoriais ativos. Ligue "Mostrar inativos" para ver os demais.' : "O catálogo ainda está vazio."}
          </p>
        </div>
      );
    }
    return null;
  };
  const listState = renderListState();

  const erroLinha = (lista, i) => formErrors[`${lista}.${i}`];

  return (
    <div className="content catalogo-tutoriais-page">
      <Modal isOpen={isFormOpen} toggle={closeForm} modalClassName={MODAL_CLASS} fade={false} size="lg">
        <CabecalhoModal titulo={editing ? `Editar tutorial · ${editing.codigo}` : "Novo tutorial"} onFechar={closeForm} />
        <ModalBody style={{ maxHeight: "75vh", overflowY: "auto", padding: "1.25rem 1.5rem" }}>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label for="tutorial-codigo">Código</Label>
                <Input
                  id="tutorial-codigo"
                  value={form.codigo}
                  maxLength={LIMITES.codigo}
                  disabled={Boolean(editing)}
                  onChange={(e) => updateForm("codigo", e.target.value)}
                  invalid={Boolean(formErrors.codigo)}
                  placeholder="ex.: despacho-indicadores"
                />
                {formErrors.codigo ? <FormFeedback>{formErrors.codigo}</FormFeedback> : <small className="catalogo-tutoriais-modal__dica">Minúsculas, números e hífen. Vai no link da Central.</small>}
              </FormGroup>
            </Col>
            <Col md="8">
              <FormGroup>
                <Label for="tutorial-titulo">Título</Label>
                <Input id="tutorial-titulo" value={form.titulo} maxLength={LIMITES.titulo} onChange={(e) => updateForm("titulo", e.target.value)} invalid={Boolean(formErrors.titulo)} placeholder="ex.: Como acompanhar o despacho" />
                {formErrors.titulo ? <FormFeedback>{formErrors.titulo}</FormFeedback> : null}
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label for="tutorial-resumo">Resumo</Label>
            <Input id="tutorial-resumo" type="textarea" rows="2" value={form.resumo} maxLength={LIMITES.resumo} onChange={(e) => updateForm("resumo", e.target.value)} invalid={Boolean(formErrors.resumo)} />
            {formErrors.resumo ? <FormFeedback>{formErrors.resumo}</FormFeedback> : null}
          </FormGroup>

          <FormGroup>
            <Label>Produtos</Label>
            <div className="catalogo-tutoriais__produtos" role="group" aria-label="Produtos">
              {PRODUTOS_TUTORIAL.map((p) => (
                <FormGroup check key={p.codigo} className="mb-0">
                  <Label check>
                    <Input
                      type="checkbox"
                      id={`tutorial-produto-${p.codigo}`}
                      checked={(form.produtos || []).indexOf(p.codigo) !== -1}
                      onChange={(e) => {
                        const marcado = e.target.checked;
                        toggleProduto(p.codigo, marcado);
                      }}
                    />
                    <span className="form-check-sign" />
                    {p.rotulo}
                  </Label>
                </FormGroup>
              ))}
            </div>
            {formErrors.produtos ? <div className="catalogo-tutoriais__linha-erro">{formErrors.produtos}</div> : null}
          </FormGroup>

          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="tutorial-permissao">Permissão exigida</Label>
                <Input id="tutorial-permissao" value={form.permissao} maxLength={LIMITES.permissao} onChange={(e) => updateForm("permissao", e.target.value)} invalid={Boolean(formErrors.permissao)} placeholder="ex.: hub.operacao.despacho.visualizar" />
                {formErrors.permissao ? <FormFeedback>{formErrors.permissao}</FormFeedback> : <small className="catalogo-tutoriais-modal__dica">Vazio = todos do produto. Várias separadas por vírgula: basta ter uma.</small>}
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label for="tutorial-data">Publicado em</Label>
                <Input id="tutorial-data" type="date" value={form.dataPublicacao} onChange={(e) => updateForm("dataPublicacao", e.target.value)} invalid={Boolean(formErrors.dataPublicacao)} />
                {formErrors.dataPublicacao ? <FormFeedback>{formErrors.dataPublicacao}</FormFeedback> : <small className="catalogo-tutoriais-modal__dica">Selo "Novo" por 30 dias.</small>}
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label for="tutorial-ordem">Ordem</Label>
                <Input id="tutorial-ordem" inputMode="numeric" value={form.ordem} onChange={(e) => updateForm("ordem", e.target.value)} invalid={Boolean(formErrors.ordem)} />
                {formErrors.ordem ? <FormFeedback>{formErrors.ordem}</FormFeedback> : null}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="tutorial-youtube">Vídeo do YouTube</Label>
                <Input id="tutorial-youtube" value={form.youtubeId} onChange={(e) => updateForm("youtubeId", e.target.value)} invalid={Boolean(formErrors.youtubeId)} placeholder="ID ou link (youtu.be/...)" />
                {formErrors.youtubeId ? <FormFeedback>{formErrors.youtubeId}</FormFeedback> : null}
              </FormGroup>
            </Col>
            <Col md="3">
              <FormGroup>
                <Label for="tutorial-duracao">Duração</Label>
                <Input id="tutorial-duracao" value={form.duracaoSegundos} onChange={(e) => updateForm("duracaoSegundos", e.target.value)} invalid={Boolean(formErrors.duracaoSegundos)} placeholder="2:45 ou 165" />
                {formErrors.duracaoSegundos ? <FormFeedback>{formErrors.duracaoSegundos}</FormFeedback> : null}
              </FormGroup>
            </Col>
            {editing ? null : (
              <Col md="3">
                <FormGroup>
                  <Label for="tutorial-status">Status</Label>
                  <Input id="tutorial-status" type="select" value={form.status} onChange={(e) => updateForm("status", e.target.value)}>
                    <option value="active">Ativo</option>
                    <option value="inactive">Inativo</option>
                  </Input>
                </FormGroup>
              </Col>
            )}
          </Row>

          <div className="catalogo-tutoriais__secao">
            <div className="catalogo-tutoriais__secao-titulo">
              <span>Capítulos do vídeo</span>
              <Button size="sm" color="info" outline onClick={() => addLinha("capitulos", { s: "", titulo: "" })}>
                <FaPlus style={{ marginRight: 4 }} /> Capítulo
              </Button>
            </div>
            {(form.capitulos || []).map((c, i) => (
              <div className="catalogo-tutoriais__linha catalogo-tutoriais__linha--capitulo" key={`cap-${i}`}>
                <Input bsSize="sm" aria-label="Tempo do capítulo" placeholder="0:35" value={c.s} onChange={(e) => updateLinha("capitulos", i, "s", e.target.value)} invalid={Boolean(erroLinha("capitulos", i))} />
                <Input bsSize="sm" aria-label="Título do capítulo" placeholder="Quadro do despacho" value={c.titulo} onChange={(e) => updateLinha("capitulos", i, "titulo", e.target.value)} invalid={Boolean(erroLinha("capitulos", i))} />
                <Button color="link" size="sm" className="btn-icon text-danger" title="Remover capítulo" onClick={() => removeLinha("capitulos", i)}>
                  <FaTrash />
                </Button>
                {erroLinha("capitulos", i) ? <div className="catalogo-tutoriais__linha-erro">{erroLinha("capitulos", i)}</div> : null}
              </div>
            ))}
          </div>

          <div className="catalogo-tutoriais__secao">
            <div className="catalogo-tutoriais__secao-titulo">
              <span>Telas do Hub (ícone "Como usar esta tela")</span>
              <Button size="sm" color="info" outline onClick={() => addLinha("telas", { rota: "", aba: "", s: "" })}>
                <FaPlus style={{ marginRight: 4 }} /> Tela
              </Button>
            </div>
            {(form.telas || []).map((t, i) => (
              <div className="catalogo-tutoriais__linha catalogo-tutoriais__linha--tela" key={`tela-${i}`}>
                <Input bsSize="sm" aria-label="Rota da tela" placeholder="/app/trackfy/dashboard" value={t.rota} onChange={(e) => updateLinha("telas", i, "rota", e.target.value)} invalid={Boolean(erroLinha("telas", i))} />
                <Input bsSize="sm" aria-label="Aba da tela" placeholder="aba (opcional)" value={t.aba} onChange={(e) => updateLinha("telas", i, "aba", e.target.value)} />
                <Input bsSize="sm" aria-label="Tempo no vídeo" placeholder="0:35" value={t.s} onChange={(e) => updateLinha("telas", i, "s", e.target.value)} invalid={Boolean(erroLinha("telas", i))} />
                <Button color="link" size="sm" className="btn-icon text-danger" title="Remover tela" onClick={() => removeLinha("telas", i)}>
                  <FaTrash />
                </Button>
                {erroLinha("telas", i) ? <div className="catalogo-tutoriais__linha-erro">{erroLinha("telas", i)}</div> : null}
              </div>
            ))}
          </div>

          <div className="catalogo-tutoriais__secao">
            <div className="catalogo-tutoriais__secao-titulo">
              <span>Passo a passo (painel lateral)</span>
              <Button size="sm" color="info" outline onClick={() => addLinha("passos", "")}>
                <FaPlus style={{ marginRight: 4 }} /> Passo
              </Button>
            </div>
            {(form.passos || []).map((p, i) => (
              <div className="catalogo-tutoriais__linha catalogo-tutoriais__linha--passo" key={`passo-${i}`}>
                <Input bsSize="sm" aria-label={`Passo ${i + 1}`} placeholder={`Passo ${i + 1}`} value={p} onChange={(e) => updateLinha("passos", i, null, e.target.value)} invalid={Boolean(erroLinha("passos", i))} />
                <Button color="link" size="sm" className="btn-icon text-danger" title="Remover passo" onClick={() => removeLinha("passos", i)}>
                  <FaTrash />
                </Button>
                {erroLinha("passos", i) ? <div className="catalogo-tutoriais__linha-erro">{erroLinha("passos", i)}</div> : null}
              </div>
            ))}
          </div>

          <div className="catalogo-tutoriais__secao">
            <FormGroup>
              <Label for="tutorial-conteudo">Texto do manual para o Autopilot</Label>
              <Input id="tutorial-conteudo" type="textarea" rows="6" value={form.conteudoTexto} onChange={(e) => updateForm("conteudoTexto", e.target.value)} placeholder="Texto do manual do CLIENTE, em Markdown (seções com ##). Nunca o manual interno." />
              <small className="catalogo-tutoriais-modal__dica">É a base da skill "dúvida de uso": o Autopilot responde só com o que estiver aqui.</small>
            </FormGroup>
          </div>

          <div className="catalogo-tutoriais__secao">
            <div className="catalogo-tutoriais__secao-titulo">
              <span>Manual em PDF</span>
            </div>
            {editing ? (
              <div className="catalogo-tutoriais__manual">
                <span style={editing.manualNomeArquivo ? undefined : mutedCellStyle}>
                  {editing.manualNomeArquivo ? `Atual: ${editing.manualNomeArquivo}` : "Nenhum manual enviado."}
                </span>
                <input ref={inputArquivoRef} type="file" accept=".pdf,application/pdf" aria-label="Arquivo do manual" onChange={handleEscolherArquivo} data-testid="tutorial-manual-arquivo" />
                <Button size="sm" color="info" outline onClick={handleEnviarManual} disabled={!arquivoManual || Boolean(erroManual) || enviandoManual}>
                  {enviandoManual ? "Enviando..." : "Enviar PDF"}
                </Button>
                <small className="catalogo-tutoriais-modal__dica" style={{ width: "100%" }}>
                  {erroManual ? <span style={{ color: AUTOPILOT_COLORS.statusDanger }}>{erroManual}</span> : MANUAL_LEGENDA}
                </small>
              </div>
            ) : (
              <small className="catalogo-tutoriais-modal__dica">Salve o tutorial para enviar o PDF.</small>
            )}
          </div>

          <div className="catalogo-tutoriais-modal__rodape">
            <Button color="default" outline size="sm" onClick={closeForm} disabled={saving || enviandoManual}>
              {editing ? "Fechar" : "Cancelar"}
            </Button>
            <Button color="info" size="sm" onClick={handleSave} disabled={saving || enviandoManual}>
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <PageHero
        eyebrow="Catálogos"
        title="Tutoriais de treinamento"
        description={
          <>
            Catálogo da Central de Treinamento, do ícone "Como usar esta tela" e do Autopilot. Incluir um tutorial é só cadastro: sem deploy.
            {podeEditar ? null : " A manutenção é feita pela Everlog."}
          </>
        }
        actions={
          <>
            <PageHeroRefreshButton onClick={loadData} loading={loading} />
            {podeEditar ? (
              <Button color="info" className="btn-sm" onClick={openCreate}>
                <FaPlus style={{ marginRight: 6 }} />
                Novo tutorial
              </Button>
            ) : null}
          </>
        }
      />

      <FilterBand
        mode="client"
        searchLabel="Buscar tutorial"
        searchHint="Código, título ou resumo"
        searchPlaceholder="Buscar por código, título ou resumo"
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        extra={
          <>
            <Input id="tutorial-filtro-produto" type="select" bsSize="sm" aria-label="Produto" value={produtoFiltro} onChange={(e) => setProdutoFiltro(e.target.value)} className="mr-2" style={{ width: "auto", minWidth: 150 }}>
              <option value="">Todos os produtos</option>
              {PRODUTOS_TUTORIAL.map((p) => (
                <option key={p.codigo} value={p.codigo}>
                  {p.rotulo}
                </option>
              ))}
            </Input>
            <FormGroup check className="mb-0 mr-2" style={{ whiteSpace: "nowrap" }}>
              <Label check>
                <Input
                  id="tutorial-mostrar-inativos"
                  type="checkbox"
                  checked={mostrarInativos}
                  onChange={(e) => {
                    const marcado = e.target.checked;
                    setMostrarInativos(marcado);
                  }}
                />
                <span className="form-check-sign" />
                Mostrar inativos
              </Label>
            </FormGroup>
            {hasFilters ? (
              <Button size="sm" color="default" outline onClick={clearFilters}>
                Limpar
              </Button>
            ) : null}
          </>
        }
      />

      <ShellCard
        className="catalogo-tutoriais-lista"
        title="Tutoriais cadastrados"
        description={inativosOcultos ? `${sorted.length} resultado(s) · ${inativosOcultos} inativo(s) oculto(s)` : `${sorted.length} resultado(s)`}
      >
        {listState ? (
          listState
        ) : (
          <TableSurface>
            <Table size="sm" responsive hover className="hub-thead-accent">
              <thead className="text-primary">
                <tr>
                  <th style={{ width: 70 }}>
                    <SortHeader label="Ordem" field="ordem" sort={sort} onSort={handleSort} />
                  </th>
                  <th>
                    <SortHeader label="Título" field="titulo" sort={sort} onSort={handleSort} />
                  </th>
                  <th>
                    <SortHeader label="Produtos" field="produtos" sort={sort} onSort={handleSort} />
                  </th>
                  <th style={{ width: 90 }}>Vídeo</th>
                  <th style={{ width: 90 }}>Manual</th>
                  <th style={{ width: 120 }}>
                    <SortHeader label="Publicado" field="dataPublicacao" sort={sort} onSort={handleSort} />
                  </th>
                  <th style={{ width: 100 }}>
                    <SortHeader label="Status" field="status" sort={sort} onSort={handleSort} />
                  </th>
                  {podeEditar ? (
                    <th className="text-right" style={{ width: 110 }}>
                      Ações
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {paged.items.map((item) => {
                  const estaAtivo = ativo(item);
                  const duracao = formatarDuracao(item.duracaoSegundos);
                  return (
                    <tr key={item.id || item.codigo}>
                      <td>{item.ordem === null || item.ordem === undefined ? "—" : item.ordem}</td>
                      <td>
                        <div>{item.titulo}</div>
                        <small style={mutedCellStyle}>{item.codigo}</small>
                      </td>
                      <td>{(item.produtos || []).map((p) => PRODUTO_ROTULO[p] || p).join(", ") || "—"}</td>
                      <td style={item.youtubeId ? undefined : mutedCellStyle}>{item.youtubeId ? duracao || "Sim" : "—"}</td>
                      <td style={item.manualNomeArquivo || item.temManual ? undefined : mutedCellStyle}>{item.manualNomeArquivo || item.temManual ? "PDF" : "—"}</td>
                      <td>{String(item.dataPublicacao || "").slice(0, 10) || "—"}</td>
                      <td>
                        <Status status={estaAtivo ? "Ativo" : "Inativo"} title={estaAtivo ? "Ativo" : "Inativo"} />
                      </td>
                      {podeEditar ? (
                        <td className="text-right">
                          <Button color="link" size="sm" className="btn-icon" title="Editar" onClick={() => openEdit(item)}>
                            <FaPen />
                          </Button>
                          {estaAtivo ? (
                            <Button color="link" size="sm" className="btn-icon text-danger" title="Inativar" onClick={() => changeStatus(item, false)} disabled={changingStatus}>
                              <FaBan />
                            </Button>
                          ) : (
                            <Button color="link" size="sm" className="btn-icon text-success" title="Ativar" onClick={() => changeStatus(item, true)} disabled={changingStatus}>
                              <FaCheck />
                            </Button>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
            <TablePagination page={paged.page} pageSize={pageSize} total={paged.total} itemLabel="tutorial(is)" onPageChange={setPage} onPageSizeChange={setPageSize} />
          </TableSurface>
        )}
      </ShellCard>
    </div>
  );
}
